import AppComponent from '../base/app-component.ts';
import type { DataTable, ColumnDef } from './data-table.ts';
import { aggiornaInvioStatini } from './invio-statini.ts';
import { buildExportQuery, scaricaFile } from '../lib/utils.ts';
import { confermaCancellaDelega } from '../lib/cancella-delega.ts';
import { raccogliDateFilters, handleDateOpChange, resetDateRanges } from '../lib/date-filter.ts';

const COLUMNS: ColumnDef[] = [
  { key: 'lavoratore.cognome', label: 'Lavoratore', type: 'link', href: '/lavoratori/{{lavoratoreId}}', text: '{{lavoratore.cognome}} {{lavoratore.nome}}', sortable: true },
  { key: 'lavoratore.codiceFiscale', label: 'CF', type: 'code', sortable: true },
  { key: 'dataNascitaLavoratore', label: 'Data nascita', type: 'date', sortable: true, emptyText: '-' },
  { key: 'telefonoLabel', label: 'Telefono', type: 'text', emptyText: '-' },
  { key: 'cassa', label: 'Cassa', type: 'text', sortable: true },
  { key: 'stato', label: 'Stato', type: 'text', sortable: true },
  { key: 'createdAt', label: 'Data', type: 'date', sortable: true },
  { key: 'note', label: 'Note', type: 'text' },
];

/** Campi filtro letti dalle select dell'offcanvas (anche quelli pilotati dai gruppi rapidi). */
const CAMPI_SELECT = ['cassa', 'stato', 'statoLavoratore', 'da_archivio', 'colore', 'modalitaRaccolta'];

/** Gruppi di bottoni rapidi da riallineare alla select corrispondente. */
const GRUPPI_RAPIDI = ['cassa', 'stato', 'da_archivio'];

export class ElencoDeleghe extends AppComponent {
  static override componentName = 'wc-elenco-deleghe';
  protected override get templateId() { return 'elenco-deleghe'; }

  private dataTable: DataTable | null = null;
  private currentSort: string | null = null;
  private currentSortDir: 'asc' | 'desc' | null = null;
  private currentFilter = '';
  private currentPage = 1;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private offcanvas: any = null;
  private messageListenerKey: symbol | null = null;

  private get statiniEnabled(): boolean {
    return this.attributeBoolean('data-statini-enabled');
  }

  private get statiniStaging(): boolean {
    return this.attributeBoolean('data-statini-staging');
  }

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      columnsJson: JSON.stringify(COLUMNS),
      statiniEnabled: this.statiniEnabled,
      statiniStaging: this.statiniStaging,
    };
  }

  protected override afterRender(): void {
    this.dataTable = this.$('wc-data-table') as DataTable | null;
    if (!this.dataTable) return;

    this.setupTableEvents();
    this.setupFiltriAvanzati();
    this.setupFiltriRapidi();
    this.setupExportExcel();
    this.listen(this, 'statini-inviati', () => {
      this.currentPage = 1;
      this.caricaDeleghe();
    });
    this.setupMessageListener();
    this.caricaDeleghe();
  }

  private setupTableEvents(): void {
    if (!this.dataTable) return;

    this.listen(this.dataTable, 'dt-page-change', (e: Event) => {
      this.currentPage = (e as CustomEvent).detail.page;
      this.caricaDeleghe();
    });

    this.listen(this.dataTable, 'dt-sort-change', (e: Event) => {
      const { sort, dir } = (e as CustomEvent).detail;
      this.currentSort = sort;
      this.currentSortDir = dir;
      this.currentPage = 1;
      this.caricaDeleghe();
    });

    this.listen(this.dataTable, 'dt-filter-change', (e: Event) => {
      this.currentFilter = (e as CustomEvent).detail.filter;
      this.currentPage = 1;
      this.caricaDeleghe();
    });

    this.listen(this.dataTable, 'dt-row-action', (e: Event) => {
      const { action, row } = (e as CustomEvent).detail;
      if (action === 'modifica') this.handleModifica(row);
      if (action === 'cancella') this.handleCancella(row);
    });
  }

  private setupFiltriAvanzati(): void {
    const offcanvasEl = this.$('[data-offcanvas="filtri"]');
    if (offcanvasEl) {
      this.offcanvas = new (globalThis as any).bootstrap.Offcanvas(offcanvasEl);
    }

    this.delegate.on('click', '[data-action="apri-filtri"]', () => {
      this.offcanvas?.show();
    });

    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', (e: Event) => {
        e.preventDefault();
        this.offcanvas?.hide();
        this.syncButtonGroups();
        this.currentPage = 1;
        this.caricaDeleghe();
      });
    }

    this.delegate.on('click', '[data-action="reset-filtri"], [data-action="reset-filtri-toolbar"]', () => {
      this.resetFiltri();
      this.offcanvas?.hide();
      this.currentPage = 1;
      this.caricaDeleghe();
    });

    this.delegate.on('change', '[data-date-op]', (e: Event) => handleDateOpChange(this, e));
  }

  private setupFiltriRapidi(): void {
    this.delegate.on('click', '[data-filtro-rapido] button', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('button') as HTMLElement;
      const group = btn.closest('[data-filtro-rapido]') as HTMLElement;
      const campo = group.dataset.filtroRapido!;
      const value = btn.dataset.value!;
      const wasActive = btn.classList.contains('active');

      group.querySelectorAll('button').forEach(b => b.classList.remove('active'));
      if (!wasActive) btn.classList.add('active');

      const select = this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null;
      if (select) select.value = wasActive ? '' : value;

      this.currentPage = 1;
      this.caricaDeleghe();
    });
  }

  private setupExportExcel(): void {
    this.delegate.on('click', '[data-action="export-excel"]', (e: Event) => {
      e.preventDefault();
      const qs = buildExportQuery(this.raccogliFiltri(), this.currentFilter);
      scaricaFile(`/api/deleghe/export-csv?${qs}`);
    });
  }

  private setupMessageListener(): void {
    const handler = (e: MessageEvent) => {
      if (e.data?.action === 'modal-close' && e.data.refresh) {
        this.caricaDeleghe();
      }
    };
    this.messageListenerKey = this.listen(window, 'message', handler as EventListener);
  }

  private syncButtonGroups(): void {
    for (const campo of GRUPPI_RAPIDI) {
      const select = this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null;
      const group = this.$(`[data-filtro-rapido="${campo}"]`);
      if (!select || !group) continue;
      const val = select.value;
      group.querySelectorAll('button').forEach(b => {
        const btn = b as HTMLElement;
        btn.classList.toggle('active', btn.dataset.value === val);
      });
    }
  }

  private raccogliFiltri(): Record<string, string> {
    const params: Record<string, string> = {};

    for (const campo of CAMPI_SELECT) {
      const val = (this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null)?.value;
      if (val) params[campo] = val;
    }

    Object.assign(params, raccogliDateFilters(this, ['dataNascita', 'dataCessazione']));

    // Data delega → backend usa da/a su created_at
    const dataDelega = raccogliDateFilters(this, ['dataDelega']);
    if (dataDelega.dataDelegaDa) params.da = dataDelega.dataDelegaDa;
    if (dataDelega.dataDelegaA) params.a = dataDelega.dataDelegaA;

    const note = (this.$('[data-filtro-val="note"]') as HTMLInputElement | null)?.value;
    if (note) params.note = note;

    return params;
  }

  private resetFiltri(): void {
    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    form?.reset();
    resetDateRanges(this);
    this.syncButtonGroups();
  }

  private updateFiltriCount(filtri: Record<string, string>): void {
    const count = Object.keys(filtri).length;
    const badge = this.$('[data-filtri-count]');
    if (badge) {
      badge.textContent = count > 0 ? String(count) : '';
      badge.classList.toggle('d-none', count === 0);
    }
    const resetBtn = this.$('[data-action="reset-filtri-toolbar"]');
    resetBtn?.classList.toggle('d-none', count === 0);
  }

  private async caricaDeleghe(): Promise<void> {
    if (!this.dataTable) return;
    this.dataTable.setLoading();

    const filtri = this.raccogliFiltri();
    this.updateFiltriCount(filtri);
    this.updateStatiniButton(filtri);

    const result = await this.api.deleghe.lista({
      ...filtri,
      page: this.currentPage,
      limit: 30,
      sort: this.currentSort ?? undefined,
      sortDir: this.currentSortDir ?? undefined,
      q: this.currentFilter || undefined,
    });

    if (!result.ok) {
      this.dataTable.setError(result.error);
      return;
    }

    const { deleghe, count, totalPages, pageSize, page } = result.data;
    const rows = deleghe.map(d => {
      const raw = d as Record<string, unknown>;
      const lav = raw.lavoratore as Record<string, unknown> | null;
      return {
        ...raw,
        telefonoLabel: lav ? (lav.cellulare || lav.telefono || null) : null,
      };
    });
    this.dataTable.setData(rows, {
      page, totalPages, count, pageSize,
    });
  }

  private async handleCancella(row: Record<string, unknown>): Promise<void> {
    const lav = row.lavoratore as Record<string, unknown> | undefined;
    const nome = `${lav?.cognome ?? ''} ${lav?.nome ?? ''}`.trim();
    if (await confermaCancellaDelega(this.api, row.id as number, nome)) this.caricaDeleghe();
  }

  private async handleModifica(row: Record<string, unknown>): Promise<void> {
    const id = row.id as number;
    const result = await Weext.modalIframe(`/deleghe/${id}/modifica?layout=modal`, {
      title: 'Modifica delega',
      size: 'lg',
    });
    if (result.refresh) {
      this.caricaDeleghe();
    }
  }

  // --- Invio a Statini ---

  /**
   * Il bottone è attivo solo con il filtro "Manuali" (da_archivio = 0) e un giorno
   * o un intervallo di Data delega: senza date l'invio riguarderebbe l'intero storico.
   */
  private updateStatiniButton(filtri: Record<string, string>): void {
    const manuali = filtri.da_archivio === '0';
    const conDate = !!(filtri.da || filtri.a);
    aggiornaInvioStatini(this.$('wc-invio-statini'), {
      filtri: this.currentFilter ? { ...filtri, q: this.currentFilter } : filtri,
      abilitato: manuali && conDate,
      tooltip: manuali
        ? (conDate ? 'Invia a Statini le deleghe filtrate' : 'Indica un giorno o un intervallo di Data delega')
        : 'Seleziona il filtro Manuali',
    });
  }

  protected override onDisconnected(): void {
    this.offcanvas?.dispose();
    if (this.messageListenerKey) {
      this.unlisten(this.messageListenerKey);
      this.messageListenerKey = null;
    }
  }
}
